import type { Key, Selection } from '@react-types/shared';
import { Combobox } from '@base-ui/react/combobox';
import * as React from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type {
  ComboBoxItemProps,
  ComboBoxPlacement,
  ComboBoxRow,
  ComboBoxRowGroup,
} from '../shared/ComboBox';
import { Icon } from '../../assets/Icon/Icon';
import { devices } from '../../common/devices';
import { selectors, transitions } from '../../controls/shared/styles';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Body } from '../../text/Body';
import { Small } from '../../text/Small';
import { useControllableState } from '../../utilities/useControllableState';
import { Badge } from '../Badge/Badge';
import { BaseInputContainer } from '../BaseInput/BaseInput';
import { CheckboxInput } from '../CheckboxInput/CheckboxInput';
import {
  COMBOBOX_MAX_ITEMS,
  ComboBoxActions,
  ComboBoxEmpty,
  ComboBoxGroup,
  ComboBoxGroupLabel,
  comboBoxIsItemEqualToValue,
  ComboBoxList,
  ComboBoxListOption,
  comboBoxPlacement,
  ComboBoxPopup,
  ComboBoxPortal,
  ComboBoxPositioner,
  comboBoxRowLabel,
  ComboBoxSearch,
  useComboBoxCollection,
  useComboBoxFilter,
  useComboBoxFilteredRows,
  useComboBoxLabel,
  useValueList,
  useValueSet,
} from '../shared/ComboBox';

export {
  ComboBoxItem as MultiComboBoxItem,
  ComboBoxSection as MultiComboBoxSection,
} from '../shared/ComboBox';

export const MultiComboBoxTagsList = styled('div', {
  cursor: 'text',
  width: '100%',

  '&:focus-visible': {
    outline: 'none',
  },

  variants: {
    size: {
      large: {
        paddingY: '$8',
        paddingX: '$12',
      },
      medium: {
        paddingY: '$4',
        paddingX: '$8',
      },
      small: {
        paddingY: '$4',
        paddingX: '$6',
      },
    },
  },
});
MultiComboBoxTagsList.displayName = 'TagsList';

const TagsListInner = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '$4',
  minHeight: '$20', // this is the height of the "small" badge size
  outline: 'none',
});
TagsListInner.displayName = 'TagsListInner';

const BadgeContainer = styled('div', {
  cursor: 'pointer',
});
BadgeContainer.displayName = 'BadgeContainer';

const MultiComboBoxBadge = styled(Badge, {
  verticalAlign: 'top',

  variants: {
    size: {
      'x-small': {
        [devices.touch]: {
          heightAll: 'unset',
        },

        [devices.mouse]: {
          heightAll: 'unset',
        },
      },
      small: {
        [devices.touch]: {
          heightAll: 'unset',
        },

        [devices.mouse]: {
          heightAll: 'unset',
        },
      },
      medium: {
        [devices.touch]: {
          heightAll: 'unset',
        },

        [devices.mouse]: {
          heightAll: 'unset',
        },
      },
      large: {
        [devices.touch]: {
          heightAll: 'unset',
        },

        [devices.mouse]: {
          heightAll: 'unset',
        },
      },
    },
  },
});
MultiComboBoxBadge.displayName = 'MultiComboBoxBadge';

const Placeholder = styled(Body, {
  display: 'block',
  color: '$$placeholderColor',
});
Placeholder.displayName = 'Placeholder';

const MultiComboBoxAdd = styled(Icon, {
  color: colors.iconNeutralLight,
  cursor: 'pointer',
  transition: transitions.control,

  [darkThemeSelector]: {
    color: colors.iconNeutralDark,
  },

  [selectors.hover]: {
    color: colors.bodyNeutralLight,

    [darkThemeSelector]: {
      color: colors.bodyNeutralDark,
    },
  },
});
MultiComboBoxAdd.displayName = 'MultiComboBoxAdd';

const MultiComboBoxIcon = styled(Icon, {
  width: '$14',
  height: '$14',
  color: colors.iconNeutralLight,
  cursor: 'pointer',
  transition: transitions.control,

  [darkThemeSelector]: {
    color: colors.iconNeutralDark,
  },

  [selectors.hover]: {
    color: colors.bodyNeutralLight,

    [darkThemeSelector]: {
      color: colors.bodyNeutralDark,
    },
  },
});
MultiComboBoxIcon.displayName = 'MultiComboBoxIcon';

const MultiComboBoxContainer = styled(BaseInputContainer, {
  alignItems: 'center',

  variants: {
    icon: {
      true: {
        paddingLeft: '$8',
      },
    },
  },
});
MultiComboBoxContainer.displayName = 'MultiComboBoxContainer';

const MultiComboBoxInnerContainer = styled('div', {
  width: '100%',
});
MultiComboBoxInnerContainer.displayName = 'MultiComboBoxInnerContainer';

/**
 * Targetable so an option can style itself differently inside the popup to how it reads in a chip.
 * See `HideInComboBoxPopover`.
 */
export const MultiComboBoxPopover = styled(ComboBoxPopup, {
  // Wide enough for a long label to wrap rather than stretch the popup across the viewport.
  maxWidth: 400,

  // Radix puts `pointer-events: none` on the body while a modal is open and restores it only on its
  // own content, which a portalled popup isn't inside. Filters open one from a `DropdownMenu`.
  // See https://github.com/radix-ui/primitives/issues/2122
  pointerEvents: 'auto',
});
MultiComboBoxPopover.displayName = 'MultiComboBoxPopover';

const SelectAllRow = styled('label', {
  display: 'flex',
  alignItems: 'center',
  gap: '$8',
  padding: '0 $4',
  cursor: 'pointer',
});
SelectAllRow.displayName = 'SelectAllRow';

const SelectAllLabel = styled(Small, {
  color: colors.bodyNeutralLight,

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },
});
SelectAllLabel.displayName = 'SelectAllLabel';

/** The Base UI spelling of react-aria's `shouldFlip={false}`. */
const NO_FLIP = { side: 'shift', align: 'shift' } as const;

const NO_VALUES: string[] = [];

// The trigger opens on mousedown, so removing a chip has to stop that as well as the click.
function stopPropagation(event: React.MouseEvent) {
  event.stopPropagation();
}

interface MultiComboBoxTagProps {
  row: ComboBoxRow;
  disabled: boolean;
  onRemove: (value: string) => void;
}

function MultiComboBoxTagImpl({ row, disabled, onRemove }: MultiComboBoxTagProps) {
  const handleClick = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!disabled) onRemove(row.value);
    },
    [disabled, onRemove, row.value],
  );

  return (
    <BadgeContainer data-multicombobox-tag onMouseDown={stopPropagation} onClick={handleClick}>
      <MultiComboBoxBadge
        icon="cross"
        variant="brand"
        size="small"
        tooltipContents={row.tooltipContents}
      >
        {row.label}
      </MultiComboBoxBadge>
    </BadgeContainer>
  );
}

const MultiComboBoxTag = React.memo(MultiComboBoxTagImpl);

interface MultiComboBoxSelectAllProps {
  disabledValues: ReadonlySet<string>;
  selectedValues: ReadonlySet<string>;
  onToggle: (rows: ComboBoxRow[], deselect: boolean) => void;
}

/**
 * Applies to the options the filter is currently showing, so it never disturbs selections made under
 * a different query. Reads the list's own filter pass rather than running a second one.
 */
function MultiComboBoxSelectAll({
  disabledValues,
  selectedValues,
  onToggle,
}: MultiComboBoxSelectAllProps) {
  const rows = useComboBoxFilteredRows();
  const selectable = React.useMemo(
    () => rows.filter((row) => !row.disabled && !disabledValues.has(row.value)),
    [disabledValues, rows],
  );

  const selectedCount = selectable.reduce(
    (count, row) => (selectedValues.has(row.value) ? count + 1 : count),
    0,
  );

  const handleChange = React.useCallback(
    () => onToggle(selectable, selectedCount > 0),
    [onToggle, selectable, selectedCount],
  );

  if (!selectable.length) return null;

  const label = selectedCount > 0 ? 'Deselect all' : 'Select all';

  return (
    <SelectAllRow>
      <CheckboxInput
        checked={selectedCount === selectable.length}
        indeterminate={selectedCount > 0 && selectedCount < selectable.length}
        aria-label={label}
        onChange={handleChange}
      />
      <SelectAllLabel>{label}</SelectAllLabel>
    </SelectAllRow>
  );
}

type MultiComboBoxSize = 'small' | 'medium' | 'large';

export type MultiComboBoxItemProps<T = unknown> = ComboBoxItemProps<T>;

interface MultiComboBoxProps<T = object> {
  'aria-label'?: string;
  /**
   * Rendered under the search field, alongside the select-all row.
   */
  actions?: React.ReactNode;
  /**
   * The list of options to render inside the popover.
   * - Use `<MultiComboBoxItem>` to render an option.
   * - Use `<MultiComboBoxSection>` to group options into sections.
   */
  children?: React.ReactNode | ((item: T) => React.ReactElement);
  defaultValue?: 'all' | Iterable<Key>;
  /**
   * @default false
   */
  disabled?: boolean;
  /**
   * Values that can't be selected, on top of any `MultiComboBoxItem` marked `disabled`.
   */
  disabledKeys?: Iterable<Key>;
  /**
   * Adds a row that selects or deselects everything the search is currently showing.
   * @default true
   */
  enableSelectAll?: boolean;
  groupSeparators?: boolean;
  icon?: IconName;
  id?: string;
  /**
   * Shows an em-dash in place of the chips, for a control standing in for a mixed selection.
   */
  indeterminate?: boolean;
  /**
   * Options for a function child, in place of listing them statically.
   */
  items?: Iterable<T>;
  /**
   * Accessible name for the list of options. Falls back to `aria-label`, then `placeholder`.
   */
  label?: React.ReactNode;
  /**
   * Withholds results until the search reaches this many characters, for lists too long to render
   * unfiltered.
   */
  minSearchLength?: number;
  /**
   * Shown in place of the results when there are no options and nothing typed, for a list fetched
   * per query.
   * @default 'No results'
   */
  noDataMessage?: string;
  /**
   * @default 'Select items'
   */
  placeholder?: string;
  /**
   * @default 'bottom start'
   */
  placement?: ComboBoxPlacement;
  ref?: React.Ref<HTMLDivElement>;
  /**
   * Replaces the chips in the trigger.
   */
  renderSelected?: (selectedItems: ComboBoxRow[]) => React.ReactNode;
  searchPlaceholder?: string;
  /**
   * Whether the popover may flip to the opposite side when there's not enough room in the preferred
   * `placement`.
   * @default true
   */
  shouldFlip?: boolean;
  /**
   * Whether to show the "add" button alongside the chips.
   * @default false
   */
  showAdd?: boolean;
  /**
   * @default 'medium'
   */
  size?: MultiComboBoxSize;
  value?: Iterable<Key>;
  /**
   * The width of the popover. `100%` matches the trigger.
   */
  width?: string | number;
  onInputChange?: (value: string) => void;
  onKeyDown?: React.KeyboardEventHandler;
  onValueChange?: (keys: Selection) => void;
}

export function MultiComboBox<T extends object = object>({
  'aria-label': ariaLabel,
  actions,
  children,
  defaultValue,
  disabled = false,
  disabledKeys,
  enableSelectAll = true,
  groupSeparators,
  icon,
  id,
  indeterminate,
  items,
  label,
  minSearchLength,
  noDataMessage,
  placeholder = 'Select items',
  placement,
  ref,
  renderSelected,
  searchPlaceholder = 'Search…',
  shouldFlip = true,
  showAdd = false,
  size = 'medium',
  value,
  width,
  onInputChange,
  onKeyDown,
  onValueChange,
}: MultiComboBoxProps<T>) {
  const collection = useComboBoxCollection(children, items);
  const disabledValues = useValueSet(disabledKeys);
  const matchesQuery = useComboBoxFilter();

  const controlledValues = useValueList(value);
  const [initialValues] = React.useState(() =>
    defaultValue === 'all'
      ? collection.rows.map((row) => row.value)
      : Array.from(defaultValue ?? NO_VALUES, String),
  );

  const handleValueChange = React.useCallback(
    (next: string[]) => {
      const keys: Set<Key> = new Set(next);
      onValueChange?.(keys);
    },
    [onValueChange],
  );

  const [selectedValues, setSelectedValues] = useControllableState<string[]>({
    defaultValue: initialValues,
    onChange: handleValueChange,
    value: controlledValues,
  });

  const selectedSet = React.useMemo(() => new Set(selectedValues), [selectedValues]);

  const selectedRows = React.useMemo(() => {
    const rows: ComboBoxRow[] = [];
    for (const selected of selectedValues) {
      const row = collection.byValue.get(selected);
      if (row) rows.push(row);
    }
    return rows;
  }, [collection, selectedValues]);

  const [query, setQuery] = React.useState('');
  const trimmedQuery = query.trim();

  // Base UI passes `filter` the query it seeds from the selection's labels rather than the one
  // being typed, so a reopened list would never reach past the rows already chosen.
  const filter = React.useCallback(
    (row: unknown) => matchesQuery(row, query),
    [matchesQuery, query],
  );

  // `BaseInputContainer` keys its focus ring off this attribute rather than `:focus`, and in
  // input-inside-popup mode focus sits in the search field while the popup is open.
  const [open, setOpen] = React.useState(false);
  const [focused, setFocused] = React.useState(false);

  // Base UI clears the search after every pick. That's the wrong half of the trade for a
  // multi-select, where the point of filtering is to tick off several matches in a row.
  const keepQueryRef = React.useRef(false);

  const handleSelectionChange = React.useCallback(
    (next: string[]) => {
      keepQueryRef.current = true;
      setSelectedValues(next);
    },
    [setSelectedValues],
  );

  const handleInputValueChange = React.useCallback(
    (next: string, details: Combobox.Root.ChangeEventDetails) => {
      const keepQuery = keepQueryRef.current;
      keepQueryRef.current = false;
      if (keepQuery && details.reason === 'input-clear') {
        details.cancel();
        return;
      }
      setQuery(next);
      onInputChange?.(next);
    },
    [onInputChange],
  );

  // Closing clears the query with the same reason a pick does, and that one has to land. Base UI
  // only clears on close-complete, so opening clears too — otherwise reopening mid-exit animation
  // would keep the last search.
  const handleOpenChange = React.useCallback((next: boolean) => {
    keepQueryRef.current = false;
    if (next) setQuery('');
    setOpen(next);
  }, []);

  const handleRemove = React.useCallback(
    (rowValue: string) => {
      setSelectedValues((current) => current.filter((entry) => entry !== rowValue));
    },
    [setSelectedValues],
  );

  const handleToggleFiltered = React.useCallback(
    (rows: ComboBoxRow[], deselect: boolean) => {
      setSelectedValues((current) => {
        const next = new Set(current);
        for (const row of rows) {
          if (deselect) next.delete(row.value);
          else next.add(row.value);
        }
        return Array.from(next);
      });
    },
    [setSelectedValues],
  );

  // Withholding the options, not the rendered list: below the threshold there's nothing to mount.
  const belowSearchLength = minSearchLength != null && trimmedQuery.length < minSearchLength;
  const searchableItems = React.useMemo(
    () => (belowSearchLength ? [] : collection.groups ?? collection.rows),
    [belowSearchLength, collection],
  );

  let emptyMessage: React.ReactNode = noDataMessage ?? 'No results';
  if (belowSearchLength) {
    emptyMessage = `Type at least ${minSearchLength} ${
      minSearchLength === 1 ? 'character' : 'characters'
    } to see results.`;
  } else if (trimmedQuery) {
    emptyMessage = `No results matching “${trimmedQuery}”`;
  }

  const popupStyle = React.useMemo(
    () => (width === '100%' ? { maxWidth: 'var(--anchor-width)' } : { width }),
    [width],
  );

  const { side, align } = React.useMemo(() => comboBoxPlacement(placement), [placement]);

  const renderRow = React.useCallback(
    (row: ComboBoxRow) => (
      <ComboBoxListOption
        key={row.value}
        row={row}
        disabled={row.disabled || disabledValues.has(row.value)}
      />
    ),
    [disabledValues],
  );

  const renderGroup = React.useCallback(
    (group: ComboBoxRowGroup) => (
      <ComboBoxGroup key={group.key} items={group.items} separated={groupSeparators}>
        {group.label != null && <ComboBoxGroupLabel>{group.label}</ComboBoxGroupLabel>}
        <Combobox.Collection>{renderRow}</Combobox.Collection>
      </ComboBoxGroup>
    ),
    [groupSeparators, renderRow],
  );

  // An explicit `Combobox.Collection` rather than a function child of the list: 1.3.0 caches the
  // first function child it sees in a module-level variable, so every combobox in the app would
  // render this one's rows.
  const listChildren = React.useMemo(
    () => <Combobox.Collection>{collection.groups ? renderGroup : renderRow}</Combobox.Collection>,
    [collection.groups, renderGroup, renderRow],
  );

  const { labelProps, labelNode } = useComboBoxLabel({
    'aria-label': ariaLabel,
    label,
    placeholder,
  });

  let triggerContents: React.ReactNode;
  if (renderSelected) {
    triggerContents = renderSelected(selectedRows);
  } else if (indeterminate) {
    triggerContents = <>&nbsp;&ndash;</>;
  } else if (selectedRows.length) {
    triggerContents = (
      <TagsListInner>
        {selectedRows.map((row) => (
          <MultiComboBoxTag key={row.value} row={row} disabled={disabled} onRemove={handleRemove} />
        ))}
        {showAdd && <MultiComboBoxAdd icon="plus" size={size === 'small' ? 10 : 14} />}
      </TagsListInner>
    );
  } else {
    triggerContents = <Placeholder>{placeholder}</Placeholder>;
  }

  return (
    <Combobox.Root<string, true>
      multiple
      disabled={disabled}
      items={searchableItems}
      filter={filter}
      limit={COMBOBOX_MAX_ITEMS}
      autoHighlight
      itemToStringLabel={comboBoxRowLabel}
      isItemEqualToValue={comboBoxIsItemEqualToValue}
      inputValue={query}
      value={selectedValues}
      onValueChange={handleSelectionChange}
      onInputValueChange={handleInputValueChange}
      onOpenChange={handleOpenChange}
    >
      <MultiComboBoxInnerContainer ref={ref}>
        {labelNode}
        <Combobox.Trigger
          nativeButton={false}
          id={id}
          {...labelProps}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(event: React.KeyboardEvent) => {
            onKeyDown?.(event);
            if (event.key === 'Backspace') setSelectedValues(NO_VALUES);
          }}
          render={
            <MultiComboBoxContainer
              as="div"
              icon={!!icon}
              size={size}
              isDisabled={disabled}
              data-is-focused={open || focused ? 'true' : undefined}
            />
          }
        >
          {icon && <MultiComboBoxIcon icon={icon} size={size === 'small' ? 12 : 16} />}
          <MultiComboBoxTagsList size={size}>{triggerContents}</MultiComboBoxTagsList>
        </Combobox.Trigger>
      </MultiComboBoxInnerContainer>
      <ComboBoxPortal>
        <ComboBoxPositioner
          align={align}
          side={side}
          collisionPadding={8}
          sideOffset={4}
          collisionAvoidance={shouldFlip ? undefined : NO_FLIP}
        >
          <MultiComboBoxPopover {...labelProps} style={popupStyle}>
            <ComboBoxSearch placeholder={searchPlaceholder} />
            <ComboBoxActions>
              {enableSelectAll && (
                <MultiComboBoxSelectAll
                  disabledValues={disabledValues}
                  selectedValues={selectedSet}
                  onToggle={handleToggleFiltered}
                />
              )}
              {actions}
            </ComboBoxActions>
            <ComboBoxList aria-label={typeof label === 'string' ? label : ariaLabel ?? placeholder}>
              {listChildren}
            </ComboBoxList>
            <ComboBoxEmpty>{emptyMessage}</ComboBoxEmpty>
          </MultiComboBoxPopover>
        </ComboBoxPositioner>
      </ComboBoxPortal>
    </Combobox.Root>
  );
}

export type { MultiComboBoxProps };
